<template>
    <div>
        <div class="gradient-box gradient-box-peach" />
        <div data-test-view="users-list">
            <l-card-content>
                <l-headline :title="$t(`usersTeams.title`)" :tabs="tabs" />

                <!-- HRIS Integration errors -->
                <div
                    v-show="
                        integrations.personio &&
                            integrations.personio.enabled &&
                            integrations.personio.lastUpdateSuccess === false
                    "
                    data-test-id="personio-error"
                >
                    <l-text-alert
                        type="danger"
                        :text="$t(`adminSettings.provisioningLastUpdateError`)"
                    />
                    <hr />
                </div>

                <div
                    v-show="
                        integrations.bamboo &&
                            integrations.bamboo.enabled &&
                            integrations.bamboo.lastUpdateSuccess === false
                    "
                    data-test-id="bamboo-error"
                >
                    <l-text-alert
                        type="danger"
                        :text="$t(`adminSettings.provisioningLastUpdateError`)"
                    />
                    <hr />
                </div>
                <!--/HRIS Integration errors -->

                <!-- Filters -->
                <div class="row tw-mb-4">
                    <div class="col-md-6">
                        <l-multi-filter
                            saved-view-type="user-team"
                            :filter-types="filterTypes"
                            :preselected-filters="selectedFilters"
                            :include-deactivated-users="true"
                            :show-custom-attributes="true"
                            @update="applyFilters"
                        />
                    </div>
                </div>
                <!--/Filters -->

                <!-- User List -->
                <div data-test-id="user-list" class="user-list">
                    <l-infinite-scroll
                        :list="list"
                        :on-list-bottom="fetchMoreUsers"
                        :attach-to-window-scroll="true"
                        :can-load-more="!!canLoadMore"
                    >
                        <template #item="{ item: user, index }">
                            <div class="row" data-test-class="user" :data-test-id="`user-${index}`">
                                <div class="col-md-4 col-xs-8 user-row">
                                    <l-profile-picture :user="user" size="m" />
                                    <span class="l-default-margin">
                                        <span class="skill-title fake-link">
                                            <span
                                                class="clickable"
                                                data-test-class="user-name"
                                                tabindex="0"
                                                :data-test-id="`user-name-${index}`"
                                                @click="openUserModal(user)"
                                                @keyup.enter="openUserModal(user)"
                                            >
                                                {{ user.firstname }} {{ user.lastname }}
                                            </span>
                                        </span>
                                        <br />
                                        <span
                                            class="color-primary clickable r-default-margin-s"
                                            tabindex="0"
                                            data-test-class="edit-user"
                                            :data-test-id="`edit-user-${index}`"
                                            @click="openUserModal(user)"
                                            @keyup.enter="openUserModal(user)"
                                        >
                                            {{ $t(`usersTeams.labelUserListEdit`) }}
                                        </span>

                                        <span
                                            v-show="user.status === 'created'"
                                            v-b-popover.hover.top="{
                                                content: $t('usersTeams.popoverUserListNew'),
                                            }"
                                            class="color-warning r-default-margin-s"
                                            data-test-class="user-new"
                                            :data-test-id="`user-new-${index}`"
                                        >
                                            {{ $t(`usersTeams.labelUserListNew`) }}
                                        </span>
                                        <span
                                            v-show="user.status === 'invited'"
                                            v-b-popover.hover.top="{
                                                content: $t('usersTeams.popoverUserListInvited'),
                                            }"
                                            class="color-warning r-default-margin-s"
                                            data-test-class="user-invited"
                                            :data-test-id="`user-invited-${index}`"
                                        >
                                            {{ $t(`usersTeams.labelUserListInvited`) }}
                                        </span>
                                        <span
                                            v-show="user.status === 'active'"
                                            v-b-popover.hover.top="{
                                                content: $t('usersTeams.popoverUserListActive'),
                                            }"
                                            class="color-grey r-default-margin-s"
                                            data-test-class="user-active"
                                            :data-test-id="`user-active-${index}`"
                                        >
                                            {{ $t(`usersTeams.labelUserListActive`) }}
                                        </span>
                                        <span
                                            v-show="user.status === 'deactivated'"
                                            v-b-popover.hover.top="{
                                                content: $t(
                                                    'usersTeams.popoverUserListDeactivated',
                                                ),
                                            }"
                                            class="color-grey r-default-margin-s"
                                            data-test-class="user-deactivated"
                                            :data-test-id="`user-deactivated-${index}`"
                                        >
                                            {{ $t(`usersTeams.labelUserListDeactivated`) }}
                                        </span>
                                        <span
                                            v-show="
                                                user.status === 'invited' ||
                                                    user.status === 'created'
                                            "
                                            class="color-primary clickable r-default-margin-s"
                                            tabindex="0"
                                            data-test-class="user-send-invite"
                                            :data-test-id="`user-send-invite-${index}`"
                                            @click="openInviteModal(user)"
                                            @keyup.enter="openInviteModal(user)"
                                        >
                                            {{ $t(`usersTeams.labelUserListSendInvite`) }}
                                        </span>
                                    </span>
                                </div>

                                <!-- Teams -->
                                <div
                                    class="col-md-6 user-row"
                                    data-test-class="user-team-row"
                                    :data-test-id="`user-team-row-${index}`"
                                >
                                    <div v-show="!user.addTeam && user.localTeamRoles.length">
                                        <span
                                            v-for="(team, teamIndex) in user.localTeamRoles"
                                            :key="team._id"
                                            class="user-team-row-item"
                                        >
                                            <span
                                                v-b-popover.hover.top="{
                                                    content: team.localTeam.name,
                                                }"
                                                class="label label-light clickable r-default-margin-s"
                                                tabindex="0"
                                                data-test-class="user-team-remove"
                                                :data-test-index="index"
                                                :data-test-team-index="teamIndex"
                                                :data-test-id="
                                                    `user-team-remove-${index}-${teamIndex}`
                                                "
                                                @click="
                                                    removeTeamMember({
                                                        teamId: team.localTeam._id,
                                                        userId: user._id,
                                                        userIndex: index,
                                                        teamIndex,
                                                    })
                                                "
                                                @keyup.enter="
                                                    removeTeamMember({
                                                        teamId: team.localTeam._id,
                                                        userId: user._id,
                                                        userIndex: index,
                                                        teamIndex,
                                                    })
                                                "
                                            >
                                                <i class="fal fa-times r-default-margin-s"></i>
                                                &nbsp;
                                                <span
                                                    data-test-class="user-team-name"
                                                    :data-test-index="index"
                                                    :data-test-team-index="teamIndex"
                                                    :data-test-id="
                                                        `user-team-name-${index}-${teamIndex}`
                                                    "
                                                >
                                                    {{ team.localTeam.name.slice(0, 10) }}
                                                </span>
                                                <span v-show="team.localTeam.name.length > 10">
                                                    ...
                                                </span>
                                            </span>
                                        </span>
                                        <span
                                            class="label label-primary clickable"
                                            tabindex="0"
                                            data-test-class="user-team-add"
                                            :data-test-index="index"
                                            :data-test-id="`user-team-add-${index}`"
                                            @click="toggleAddTeamMember({ user, show: true })"
                                            @keyup.enter="toggleAddTeamMember({ user, show: true })"
                                        >
                                            <i class="fal fa-plus"></i>
                                            {{ $t(`usersTeams.labelUserListTeamAdd`) }}
                                        </span>
                                    </div>

                                    <form v-show="user.addTeam || !user.localTeamRoles.length">
                                        <div class="form-group">
                                            <div class="input-group">
                                                <l-input-typeahead
                                                    :label="''"
                                                    :placeholder="
                                                        $t(`usersTeams.placeholderSearchTeam`)
                                                    "
                                                    :data="teamsSearch"
                                                    :on-select="
                                                        typeaheadAddTeamMemberOnSelect({ user })
                                                    "
                                                    :on-input-change="
                                                        typeaheadAddTeamMemberOnInputChange
                                                    "
                                                    :remove-margin="true"
                                                    :show-picture="false"
                                                    data-test-class="user-team-add-input"
                                                    :data-test-id="`user-team-add-input-${index}`"
                                                />

                                                <div
                                                    v-show="user.addTeam"
                                                    class="input-group-addon"
                                                    style="padding-bottom: 15px"
                                                >
                                                    <i
                                                        class="fal fa-times clickable"
                                                        tabindex="0"
                                                        @click="
                                                            toggleAddTeamMember({
                                                                user,
                                                                show: false,
                                                            })
                                                        "
                                                        @keyup.enter="
                                                            toggleAddTeamMember({
                                                                user,
                                                                show: false,
                                                            })
                                                        "
                                                    ></i>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <!--/Teams -->

                                <!-- Admins -->
                                <div
                                    class="col-md-2 user-row"
                                    data-test-class="user-admin-row"
                                    :data-test-id="`user-admin-row-${index}`"
                                >
                                    <span
                                        v-show="user.teamRole.role === 'superAdmin'"
                                        data-test-class="user-admin-super-admin"
                                        :data-test-id="`user-admin-super-admin-${index}`"
                                    >
                                        <i class="fal fa-user r-default-margin-s"></i>
                                        {{ $t(`usersTeams.labelUserListSuperAdmin`) }}
                                    </span>

                                    <span
                                        v-show="user.teamRole.role == 'member'"
                                        :data-test-index="index"
                                        tabindex="0"
                                    >
                                        <i class="fal fa-user r-default-margin-s"></i>
                                        {{ $t(`usersTeams.labelUserListUser`) }}
                                    </span>

                                    <span v-show="user.teamRole.role == 'manager'" tabindex="0">
                                        <i class="fal fa-user r-default-margin-s"></i>
                                        {{ $t(`usersTeams.labelUserListAdmin`) }}
                                    </span>
                                </div>
                                <!--/Admins -->
                            </div>
                        </template>
                    </l-infinite-scroll>
                </div>
                <!--/User List -->
            </l-card-content>

            <!-- Modal -->
            <l-modal
                v-show="showModal"
                size="m"
                :close-on-backdrop-click="false"
                :show-close-btn-header="showCloseBtnHeader"
                @close="closeModal"
            >
                <template #header>{{ modalTitle }}</template>
                <template #body>
                    <router-view :close="closeModal"></router-view>
                </template>
            </l-modal>
            <!--/Modal -->
            <l-confirmation-modal
                v-if="exportOneTimePasswordsWarning"
                :text="$t('usersTeams.manageUsers.exportOneTimePasswordsWarning')"
                @confirm="exportOneTimePasswords()"
                @cancel="exportOneTimePasswordsWarning = false"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInfiniteScroll from "@/components/LInfiniteScroll";
import LInputTypeahead from "@/components/LInputTypeahead";
import LModal from "@/components/LModal";
import LProfilePicture from "@/components/LProfilePicture";
import LTextAlert from "@/components/LTextAlert";
import LMultiFilter from "@/components/LMultiFilter.vue";
import LConfirmationModal from "@/components/LConfirmationModal";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInfiniteScroll,
        LInputTypeahead,
        LModal,
        LProfilePicture,
        LTextAlert,
        LConfirmationModal,
        LMultiFilter,
    },
    props: {
        query: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            list: [],
            offset: 0,
            showModal: false,
            showCloseBtnHeader: true,
            modalTitle: "",
            selectedFilters: {},
            previousQuery: {},
            exportOneTimePasswordsWarning: false,
        };
    },
    computed: {
        ...mapGetters({
            teamsSearchList: "teams/search/list",
            getTeamIntegrations: "teams/current/integrations",
            getUser: "users/details/details",
            getUserList: "users/list/list",
            currentUser: "users/current/user",
            currentUserTeamRole: "users/current/teamRole",
        }),
        integrations() {
            return this.getTeamIntegrations();
        },
        teamsSearch() {
            return this.teamsSearchList.map(team => ({
                _id: team._id,
                name: team.name,
            }));
        },
        filterTypes() {
            return [
                {
                    value: "users",
                    key: "users",
                    text: this.$t("usersTeams.filterUsers"),
                },
                {
                    value: "status",
                    key: "status",
                    text: this.$t("usersTeams.filterStatus"),
                    options: [
                        { _id: "created", name: this.$t(`usersTeams.labelUserListNew`) },
                        { _id: "invited", name: this.$t(`usersTeams.labelUserListInvited`) },
                        { _id: "active", name: this.$t(`usersTeams.labelUserListActive`) },
                        {
                            _id: "deactivated",
                            name: this.$t(`usersTeams.labelUserListDeactivated`),
                        },
                    ],
                },
                {
                    value: "localTeams",
                    key: "localTeams",
                    text: this.$t("usersTeams.filterLocalTeams"),
                },
                {
                    value: "managers",
                    key: "managers",
                    text: this.$t("usersTeams.filterManagers"),
                },
                {
                    value: "locations",
                    key: "locations",
                    text: this.$t("usersTeams.filterLocations"),
                },
                {
                    value: "userRoles",
                    key: "userRoles",
                    text: this.$t("usersTeams.filterUserRoles"),
                    options: [
                        {
                            _id: "member",
                            name: this.$t(`usersTeams.filterUserRoleMember`),
                        },
                        {
                            _id: "manager",
                            name: this.$t(`usersTeams.filterUserRoleManager`),
                        },
                        {
                            _id: "superAdmin",
                            name: this.$t(`usersTeams.filterUserRoleSuperAdmin`),
                        },
                    ],
                },
                {
                    value: "customRoles",
                    key: "customRoles",
                    text: this.$t("usersTeams.filterCustomRoles"),
                },
                {
                    value: "admin",
                    key: "admin",
                    text: this.$t("usersTeams.filterAdmin"),
                    options: [
                        {
                            _id: "noManager",
                            name: this.$t(`usersTeams.filterAdminNoManager`),
                        },
                        {
                            _id: "noTeam",
                            name: this.$t(`usersTeams.filterAdminNoTeam`),
                        },
                    ],
                },
            ];
        },
        tabs() {
            return [
                {
                    label: this.$t("usersTeams.tabsManageTeams"),
                    show: true,
                    handler: this.showTeams,
                    testId: "manage-teams",
                },
                {
                    label: this.$t("usersTeams.tabsManageLevels"),
                    show: true,
                    handler: this.showLevels,
                    testId: "manage-levels",
                },
                {
                    label: this.$t("usersTeams.tabsManageCustomAttributes"),
                    show: true,
                    handler: this.showCustomAttributes,
                    testId: "manage-custom-attributes",
                },
                {
                    label: this.$t("usersTeams.tabsSendInvites"),
                    show: true,
                    handler: this.openInviteModal,
                    testId: "send-invites",
                },

                {
                    label: this.$t("usersTeams.tabsExportUsers"),
                    path: "",
                    show: true,
                    handler: this.exportUsers,
                    testId: "export-users",
                },
                {
                    label: this.$t("usersTeams.manageUsers.exportOneTimePasswords"),
                    popover: this.$t("usersTeams.manageUsers.exportOneTimePasswordsHelp"),
                    path: "",
                    show: true,
                    handler: () => {
                        this.exportOneTimePasswordsWarning = true;
                    },
                    testId: "export-users",
                },
                {
                    label: this.$t("usersTeams.tabsOrgChart"),
                    icon: "fal fa-external-link",
                    path: "/orgchart",
                    openNewWindow: true,
                    show: true,
                    testId: "org-chart",
                },

                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t("usersTeams.tabsAddUsers"),
                    show: true,
                    handler: this.openAddUsersModal,
                    isButton: true,
                    tabClass: "btn-primary",
                    testId: "add-users",
                },
            ];
        },
        canLoadMore() {
            return this.list.length && this.offset < this.list.length;
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                if (newRoute.name === "UsersTeams") {
                    this.showModal = false;
                }
            },
        },
    },
    created() {
        this.selectedFilters.status = ["active", "invited", "created"];
        this.selectedFilters.deactivatedByDefault = true; // include deactivated users if no status filter is chosen
        this.fetchUserList();
    },
    methods: {
        exportUsers() {
            this.$store.dispatch({
                type: "users/list/export",
                payload: { team: this.currentUserTeamRole.team },
            });
        },
        exportOneTimePasswords() {
            this.$store.dispatch({
                type: "users/list/exportOneTimePasswords",
            });
            this.exportOneTimePasswordsWarning = false;
        },
        applyFilters(filters) {
            this.selectedFilters = filters;
            this.fetchUserList();
        },
        fetchUserList(offset) {
            this.offset = offset || 0;

            return this.$store
                .dispatch({
                    type: "users/list/get",
                    payload: { userFilter: { include: this.selectedFilters }, offset },
                })
                .then(this.updateDisplayList);
        },
        fetchMoreUsers() {
            if (this.canLoadMore) {
                this.fetchUserList(this.list.length);
            }
        },
        updateDisplayList() {
            this.list = this.getUserList();
        },
        toggleAddTeamMember({ user, show }) {
            this.$set(user, "addTeam", show);
            this.$forceUpdate();
        },
        typeaheadAddTeamMemberOnSelect({ user }) {
            return async team => {
                await this.$store.dispatch({
                    type: "teams/local/addUser",
                    payload: { teamId: team._id, userId: user._id, role: "member" },
                });

                this.$store.dispatch("teams/search/reset");
                this.toggleAddTeamMember({ user, show: false });

                this.fetchUserList();
            };
        },
        typeaheadAddTeamMemberOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("teams/search/reset");
            }
        },
        async removeTeamMember({ teamId, userId }) {
            this.selectedUser = userId;
            await this.$store.dispatch({
                type: "teams/local/removeUser",
                payload: { teamId, userId },
            });
            this.fetchUserList();
        },
        openUserModal(user) {
            this.selectedUser = user._id;
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`usersTeams.modal.title.UsersTeamsManageUser`);
            this.$router.push({
                path: `/team/members/user/${user._id}`,
                query: { keepExistingList: true },
            });
        },
        openInviteModal(user) {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`usersTeams.modal.title.UsersTeamsSendInvite`);
            if (user) {
                this.$router.push({
                    path: `/team/members/invite/${user._id}`,
                    query: { keepExistingList: true },
                });
            } else {
                this.$router.push({
                    path: `/team/members/invite/all`,
                    query: { keepExistingList: true },
                });
            }
        },
        openAddUsersModal() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`usersTeams.modal.title.UsersTeamsAddUsers`);
            this.$router.push({ path: `/team/members/add`, query: { keepExistingList: true } });
        },
        showTeams() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillsManageTeams`);
            this.$router.push({
                path: `/team/members/teams/list`,
                query: { keepExistingList: true },
            });
        },
        showLevels() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(`skills.modal.title.SkillsManageLevels`);
            this.$router.push({
                path: `/team/members/levels/list`,
                query: { keepExistingList: true },
            });
        },
        showCustomAttributes() {
            this.showModal = true;
            this.showCloseBtnHeader = true;
            this.modalTitle = this.$i18n.t(
                `usersTeams.modal.title.UsersTeamsManageCustomAttributes`,
            );
            this.$router.push({
                path: `/team/members/customAttributes/list`,
                query: { keepExistingList: true },
            });
        },
        closeModal() {
            this.showModal = false;
            this.$router.push({ path: `/team/members`, query: { keepExistingList: true } });
            this.fetchUserList();
        },
    },
};
</script>

<style scoped>
.user-team-row-item {
    display: inline-block;
}
</style>
